import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Button,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Snackbar,
  Alert,
  Stack,
  Divider,
  Paper,
  Chip,
  Avatar,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { eventService } from '../services';
import { Event, EventFilters } from '../../../types/types';
import {
  Visibility,
  PlayArrow,
  Pause,
  Cancel,
  FilterList,
  Search,
  Edit,
  CheckCircle,
  Close as CloseIcon,
  Assignment,
  Place,
  CalendarMonth,
} from '@mui/icons-material';

const EVENT_THUMB_PLACEHOLDER =
  'data:image/svg+xml,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 120 120"><rect fill="#eee" width="120" height="120" rx="8"/><text x="60" y="68" text-anchor="middle" fill="#888" font-size="12" font-family="sans-serif">Evento</text></svg>',
  );

const statusLabels: Record<string, string> = {
  published: 'Publicado',
  draft: 'Rascunho',
  cancelled: 'Cancelado',
  pre_published: 'Pré-publicado',
  paused: 'Pausado',
};

const statusColors: Record<string, string> = {
  published: 'success',
  draft: 'warning',
  cancelled: 'error',
  pre_published: 'info',
  paused: 'warning',
};

/** Botões “soft” no estilo do painel de aprovação (pill + squircles). */
const sxSoftPillPrimary = {
  borderRadius: 999,
  px: 2.75,
  py: 1,
  textTransform: 'none' as const,
  fontWeight: 700,
  bgcolor: 'rgba(46, 125, 50, 0.12)',
  color: 'success.dark',
  boxShadow: 'none',
  '&:hover': { bgcolor: 'rgba(46, 125, 50, 0.22)', boxShadow: 'none' },
};

const sxSoftSquircleWarning = {
  borderRadius: 2,
  bgcolor: 'rgba(255, 193, 7, 0.18)',
  color: '#b28704',
  width: 44,
  height: 44,
  '&:hover': { bgcolor: 'rgba(255, 193, 7, 0.28)' },
};

const sxSoftSquircleDanger = {
  borderRadius: 2,
  bgcolor: 'rgba(211, 47, 47, 0.08)',
  border: '1px solid',
  borderColor: 'rgba(211, 47, 47, 0.35)',
  color: 'error.dark',
  width: 44,
  height: 44,
  '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.14)' },
};

/** Barra de ações mobile: mesmas cores “soft” do desktop (pill verde + âmbar + perigo suave). */
const sxMobileCardApproveBtn = {
  ...sxSoftPillPrimary,
  flex: 1,
  py: 1.35,
  px: 1,
  minWidth: 0,
  fontSize: '0.875rem',
};

const sxMobileCardPauseBtn = {
  flex: 1,
  py: 1.35,
  px: 1,
  minWidth: 0,
  borderRadius: 2,
  textTransform: 'none' as const,
  fontWeight: 600,
  fontSize: '0.875rem',
  bgcolor: 'rgba(255, 193, 7, 0.18)',
  color: '#b28704',
  boxShadow: 'none',
  '&:hover': { bgcolor: 'rgba(255, 193, 7, 0.28)', boxShadow: 'none' },
};

const sxMobileCardRejectBtn = {
  ...sxSoftSquircleDanger,
  width: 50,
  height: 50,
  flexShrink: 0,
};

const categoryOptions = [
  'Missa', 'Festa', 'Retiro', 'Palestra', 'Curso', 'Outro'
];

const PAGARME_APPROVE_BLOCKED_HINT =
  'Aprovação liberada independentemente do status do recebedor Pagar.me.';

function isApproveBlockedByParishPagarme(_event: Event): boolean {
  return false;
}

function EventPagarmeStatusChip({ event }: { event: Event }) {
  if (!event.requiresParishPagarmeForApproval) {
    return (
      <Tooltip title="Sem ingressos/loja pagos — Pagarme não se aplica a este evento.">
        <Chip
          label="Sem cobrança"
          size="small"
          variant="outlined"
          sx={{ height: 22, fontSize: 10, fontWeight: 700 }}
        />
      </Tooltip>
    );
  }

  if (event.pagarmeCheckoutReady) {
    return (
      <Tooltip title="Checkout Pix/cartão via Pagar.me com split configurado.">
        <Chip
          label="Pagarme OK"
          size="small"
          color="success"
          sx={{ height: 22, fontSize: 10, fontWeight: 800 }}
        />
      </Tooltip>
    );
  }

  if (event.pagarmePixCheckoutEnabled === true) {
    return (
      <Tooltip title="Evento preparado para Pagarme, mas o recebedor da paróquia ainda não está sincronizado.">
        <Chip
          label="Pagarme pendente"
          size="small"
          color="warning"
          sx={{ height: 22, fontSize: 10, fontWeight: 800 }}
        />
      </Tooltip>
    );
  }

  return (
    <Tooltip title="Checkout permanece no Mercado Pago (evento legado ou Pagarme desligado no evento).">
      <Chip
        label="Mercado Pago"
        size="small"
        sx={{
          height: 22,
          fontSize: 10,
          fontWeight: 700,
          bgcolor: 'grey.200',
          color: 'text.secondary',
        }}
      />
    </Tooltip>
  );
}

// Utilitários para datas
function formatDate(date: string | Date | undefined): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('pt-BR');
}

function formatDateTime(date: string | Date | undefined): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

/** Ex.: "12 de abr. de 2026" → próximo ao layout de referência */
function formatDateShortPt(date: string | Date | undefined): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short', year: 'numeric' });
}

function getStatusMuiSeverity(eventStatus: string | undefined): 'success' | 'info' | 'warning' | 'error' {
  const color = statusColors[eventStatus as keyof typeof statusColors];
  if (color === 'success' || color === 'info' || color === 'warning' || color === 'error') return color;
  return 'info';
}

const Events: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  /** Abaixo de md não usamos tabela larga — evita scroll horizontal. */
  const isCompactLayout = useMediaQuery(theme.breakpoints.down('md'));
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<EventFilters>({
    page: 1,
    limit: 10,
    search: '',
    status: undefined,
    category: '',
  });
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusReason, setStatusReason] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const [approvalDialog, setApprovalDialog] = useState<{ open: boolean; event: Event | null; action: 'approve' | 'reject' | null }>({ open: false, event: null, action: null });
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [scheduleItems, setScheduleItems] = useState<Array<{ id: string, title: string, dateTime: string, location?: string, description?: string }>>([]);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  /** Linha da tabela: painel rápido com resumo + ações (estilo soft UI). */
  const [quickActionsEvent, setQuickActionsEvent] = useState<Event | null>(null);
  const [showAllPending, setShowAllPending] = useState(false);

  const PENDING_PREVIEW = 6;

  const generalFilters = useMemo<EventFilters>(() => {
    const excludeStatus = filters.status ? undefined : 'pre_published';
    return { ...filters, excludeStatus };
  }, [filters]);

  const { data: pendingData, isLoading: pendingLoading, error: pendingError } = useQuery({
    queryKey: ['events', 'pending-approval', filters.search, filters.category, filters.startDate, filters.endDate],
    queryFn: () =>
      eventService.getAll({
        status: 'pre_published',
        page: 1,
        limit: 500,
        search: filters.search?.trim() || undefined,
        category: filters.category || undefined,
        startDate: filters.startDate,
        endDate: filters.endDate,
      }),
  });

  const { data: eventsData, isLoading, error } = useQuery({
    queryKey: ['events', 'general', generalFilters],
    queryFn: () => eventService.getAll(generalFilters),
  });

  // Update event status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status, reason }: { id: string; status: string; reason?: string }) =>
      eventService.updateStatus(id, status, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      setSnackbar({ open: true, message: 'Status atualizado com sucesso.', severity: 'success' });
      setShowStatusDialog(false);
      setSelectedEvent(null);
      setStatusReason('');
    },
    onError: (error: any) => {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Erro ao atualizar status do evento.', severity: 'error' });
    },
  });

  // Approve event mutation
  const approveEventMutation = useMutation({
    mutationFn: (id: string) => eventService.approve(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      setSnackbar({ open: true, message: 'Evento aprovado com sucesso.', severity: 'success' });
    },
    onError: (error: any) => {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Erro ao aprovar evento.', severity: 'error' });
    },
  });

  // Reject event mutation
  const rejectEventMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => eventService.reject(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      setSnackbar({ open: true, message: 'Evento rejeitado com sucesso.', severity: 'success' });
    },
    onError: (error: any) => {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Erro ao rejeitar evento.', severity: 'error' });
    },
  });

  const handleFilterChange = (key: keyof EventFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleViewDetails = (event: Event) => {
    setSelectedEvent(event);
    setShowDetailsDialog(true);
  };

  const handleStatusChange = (event: Event, status: string) => {
    setSelectedEvent(event);
    setNewStatus(status);
    setShowStatusDialog(true);
  };

  const handleApproveEvent = (event: Event) => {
    setApprovalDialog({ open: true, event, action: 'approve' });
  };

  const handleRejectEvent = (event: Event) => {
    setApprovalDialog({ open: true, event, action: 'reject' });
  };

  const handleEditEvent = (event: Event) => {
    setEditingEvent(event);
    setShowEditDialog(true);
  };

  const openEventQuickPanel = (event: Event) => {
    setQuickActionsEvent(event);
  };

  const closeEventQuickPanel = () => setQuickActionsEvent(null);

  const goToFullDetailsFromQuickPanel = (event: Event) => {
    setQuickActionsEvent(null);
    handleViewDetails(event);
  };

  const goToEditFromQuickPanel = (event: Event) => {
    setQuickActionsEvent(null);
    handleEditEvent(event);
  };

  const confirmApproval = () => {
    if (!approvalDialog.event || !approvalDialog.action) return;
    
    if (approvalDialog.action === 'approve') {
      approveEventMutation.mutate(approvalDialog.event._id);
    } else {
      rejectEventMutation.mutate({
        id: approvalDialog.event._id,
        reason: 'Rejeitado pelo administrador'
      });
    }
    
    setApprovalDialog({ open: false, event: null, action: null });
  };

  const handleConfirmStatusChange = () => {
    if (!selectedEvent) return;
    
    // Se for rejeição de evento em pré-publicação, usar a função de rejeição
    if (selectedEvent.status === 'pre_published' && newStatus === 'cancelled') {
      rejectEventMutation.mutate({
        id: selectedEvent._id,
        reason: statusReason,
      });
    } else {
      updateStatusMutation.mutate({
        id: selectedEvent._id,
        status: newStatus,
        reason: newStatus === 'cancelled' ? statusReason : undefined,
      });
    }
  };


  // Handlers para adicionar/remover itens
  const handleAddScheduleItem = () => {
    setScheduleItems(prev => ([...prev, { id: Date.now().toString(), title: '', dateTime: '', location: '', description: '' }]));
  };
  const handleRemoveScheduleItem = (id: string) => {
    setScheduleItems(prev => prev.filter(item => item.id !== id));
  };
  const handleChangeScheduleItem = (id: string, field: string, value: string) => {
    setScheduleItems(prev => prev.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const events = eventsData?.data?.events || [];
  const pagination = eventsData?.data?.pagination;

  const pendingEvents = pendingData?.data?.events || [];
  const pendingTotal = pendingData?.data?.pagination?.total ?? pendingEvents.length;
  const showApprovalStrip = !filters.status && pendingEvents.length > 0;
  const pendingToShow = showAllPending ? pendingEvents : pendingEvents.slice(0, PENDING_PREVIEW);

  const handleDeferPending = (ev: Event) => {
    if (!window.confirm(`Colocar "${ev.title}" em pausa?`)) return;
    updateStatusMutation.mutate({ id: ev._id, status: 'paused' });
  };

  return (
    <Box
      sx={{
        width: 1,
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        overflowX: 'hidden',
      }}
    >
      <Typography variant="h4" gutterBottom sx={{ typography: { xs: 'h5', sm: 'h4' } }}>
        Monitoramento de Eventos
      </Typography>
      <Typography variant="body1" gutterBottom sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>
        Monitore todos os eventos da plataforma, gerencie status e acompanhe métricas
      </Typography>

      {/* Filtros */}
      <Card sx={{ mb: 4, maxWidth: '100%', overflow: 'hidden' }}>
        <CardHeader title={<Box display="flex" alignItems="center" gap={1}><FilterList /> Filtros</Box>} />
        <CardContent sx={{ px: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
          <Box
            display="grid"
            gap={2}
            sx={{
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(auto-fit, minmax(200px, 1fr))',
              },
            }}
          >
            <TextField
              label="Buscar"
              variant="outlined"
              onChange={(e) => handleFilterChange('search', e.target.value)}
              InputProps={{ startAdornment: <Search sx={{ mr: 1 }} /> }}
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="published">Publicado</MenuItem>
                <MenuItem value="draft">Rascunho</MenuItem>
                <MenuItem value="cancelled">Cancelado</MenuItem>
                <MenuItem value="pre_published">Pré-publicado</MenuItem>
                <MenuItem value="paused">Pausado</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Categoria</InputLabel>
              <Select
                label="Categoria"
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
              >
                <MenuItem value="">Todas</MenuItem>
                {categoryOptions.map((cat) => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Data Início"
              type="date"
              InputLabelProps={{ shrink: true }}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
            />
            <TextField
              label="Data Fim"
              type="date"
              InputLabelProps={{ shrink: true }}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Fila de aprovação (cards) — só com filtro “Todos” e havendo pré-publicados */}
      {showApprovalStrip && (
        <Card sx={{ mb: 4, maxWidth: '100%', overflow: 'hidden' }} elevation={0} variant="outlined">
          <CardHeader
            avatar={
              <Avatar variant="rounded" sx={{ bgcolor: 'error.light', color: 'error.dark', borderRadius: 2 }}>
                <Assignment />
              </Avatar>
            }
            title={
              <Typography variant="h6" component="span" fontWeight={700}>
                Solicitações de Aprovação
              </Typography>
            }
            subheader="Eventos aguardando revisão editorial para publicação imediata."
            action={
              pendingTotal > PENDING_PREVIEW ? (
                <Button
                  variant="text"
                  color="primary"
                  onClick={() => setShowAllPending((v) => !v)}
                  fullWidth={isMobile}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    textAlign: isMobile ? 'center' : 'inherit',
                  }}
                >
                  {showAllPending ? 'Mostrar menos' : `Ver todas as ${pendingTotal} solicitações`}
                </Button>
              ) : undefined
            }
            sx={{
              alignItems: 'flex-start',
              flexDirection: { xs: 'column', sm: 'row' },
              '& .MuiCardHeader-content': { minWidth: 0 },
              '& .MuiCardHeader-action': {
                alignSelf: { xs: 'stretch', sm: 'auto' },
                m: 0,
                mt: { xs: 1, sm: 0 },
                width: { xs: '100%', sm: 'auto' },
              },
            }}
          />
          <CardContent sx={{ pt: 0, px: { xs: 1.5, sm: 2 } }}>
            {pendingLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={32} />
              </Box>
            ) : pendingError ? (
              <Alert severity="error">Erro ao carregar solicitações de aprovação.</Alert>
            ) : (
              <Stack spacing={2}>
                {pendingToShow.map((event) => {
                  const parishLine = [event.parishName || 'N/A', event.state].filter(Boolean).join(', ');
                  const mutationLocked =
                    approveEventMutation.isPending ||
                    rejectEventMutation.isPending ||
                    updateStatusMutation.isPending;
                  const pagarmeBlocksApprove = isApproveBlockedByParishPagarme(event);
                  const approveDisabled = mutationLocked || pagarmeBlocksApprove;

                  if (isMobile) {
                    return (
                      <Paper
                        key={event._id}
                        elevation={2}
                        sx={{
                          borderRadius: 2,
                          overflow: 'hidden',
                          bgcolor: 'background.paper',
                          border: '1px solid',
                          borderColor: 'divider',
                          maxWidth: '100%',
                          boxSizing: 'border-box',
                        }}
                      >
                        <Box
                          role="button"
                          tabIndex={0}
                          aria-label={`Abrir detalhes: ${event.title}`}
                          onClick={() => openEventQuickPanel(event)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              openEventQuickPanel(event);
                            }
                          }}
                          sx={{
                            cursor: 'pointer',
                            textAlign: 'left',
                            border: 'none',
                            bgcolor: 'transparent',
                            p: 0,
                            width: 1,
                            display: 'block',
                            '&:focus-visible': {
                              outline: `2px solid ${theme.palette.primary.main}`,
                              outlineOffset: 2,
                            },
                          }}
                        >
                          <Box
                            component="img"
                            src={event.coverImage || EVENT_THUMB_PLACEHOLDER}
                            alt=""
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = EVENT_THUMB_PLACEHOLDER;
                            }}
                            sx={{
                              width: 1,
                              height: 152,
                              objectFit: 'cover',
                              display: 'block',
                              bgcolor: 'action.hover',
                            }}
                          />
                          <Box sx={{ p: 2 }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} flexWrap="wrap" useFlexGap>
                              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Chip
                                  label={(event.category || '—').toUpperCase()}
                                  size="small"
                                  sx={{
                                    height: 26,
                                    fontWeight: 800,
                                    fontSize: 10,
                                    letterSpacing: 0.5,
                                    bgcolor: 'rgba(37, 99, 235, 0.14)',
                                    color: '#1e40af',
                                    borderRadius: 1,
                                  }}
                                />
                                <EventPagarmeStatusChip event={event} />
                              </Stack>
                              <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0, fontSize: '0.8rem' }}>
                                {formatDateShortPt(event.startDate)}
                              </Typography>
                            </Stack>
                            <Typography
                              variant="h6"
                              component="h3"
                              fontWeight={800}
                              sx={{ mt: 1.25, lineHeight: 1.25, wordBreak: 'break-word' }}
                            >
                              {event.title}
                            </Typography>
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1 }}>
                              <Place sx={{ fontSize: 18, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                {parishLine}
                              </Typography>
                            </Stack>
                          </Box>
                        </Box>
                        <Box sx={{ px: 2, pb: 2, pt: 0, maxWidth: '100%', boxSizing: 'border-box' }}>
                          <Stack spacing={1} sx={{ width: 1, maxWidth: '100%' }}>
                            <Tooltip
                              title={PAGARME_APPROVE_BLOCKED_HINT}
                              placement="top"
                              arrow
                              disableHoverListener={!pagarmeBlocksApprove}
                            >
                              <span style={{ width: '100%' }}>
                                <Button
                                  fullWidth
                                  variant="text"
                                  sx={{ ...sxMobileCardApproveBtn, flex: 'none', maxWidth: '100%' }}
                                  startIcon={
                                    approveEventMutation.isPending && approveEventMutation.variables === event._id ? (
                                      <CircularProgress size={18} sx={{ color: 'success.dark' }} />
                                    ) : (
                                      <CheckCircle sx={{ fontSize: 22, color: 'success.dark' }} />
                                    )
                                  }
                                  onClick={() => handleApproveEvent(event)}
                                  disabled={approveDisabled}
                                >
                                  Aprovar
                                </Button>
                              </span>
                            </Tooltip>
                            {pagarmeBlocksApprove ? (
                              <Typography variant="caption" color="warning.dark" sx={{ display: 'block', lineHeight: 1.35 }}>
                                Recebedor Pagar.me da paróquia ainda não está pronto; aprovação bloqueada.
                              </Typography>
                            ) : null}
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ width: 1, minWidth: 0 }}>
                              <Button
                                fullWidth
                                variant="text"
                                sx={{ ...sxMobileCardPauseBtn, flex: 1, minWidth: 0, maxWidth: '100%' }}
                                startIcon={
                                  updateStatusMutation.isPending && updateStatusMutation.variables?.id === event._id ? (
                                    <CircularProgress size={22} sx={{ color: '#b28704' }} />
                                  ) : (
                                    <Pause sx={{ fontSize: 22, color: '#b28704' }} />
                                  )
                                }
                                onClick={() => handleDeferPending(event)}
                                disabled={mutationLocked}
                              >
                                Pausar
                              </Button>
                              <IconButton
                                aria-label="Rejeitar solicitação"
                                sx={sxMobileCardRejectBtn}
                                onClick={() => handleRejectEvent(event)}
                                disabled={mutationLocked}
                              >
                                {rejectEventMutation.isPending && rejectEventMutation.variables?.id === event._id ? (
                                  <CircularProgress size={22} color="error" />
                                ) : (
                                  <CloseIcon sx={{ color: 'error.dark' }} />
                                )}
                              </IconButton>
                            </Stack>
                          </Stack>
                        </Box>
                      </Paper>
                    );
                  }

                  return (
                    <Paper
                      key={event._id}
                      variant="outlined"
                      role="button"
                      aria-label={`Abrir detalhes: ${event.title}`}
                      onClick={() => openEventQuickPanel(event)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openEventQuickPanel(event);
                        }
                      }}
                      tabIndex={0}
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 2,
                        flexDirection: 'row',
                        alignItems: 'center',
                        cursor: 'pointer',
                        bgcolor: 'background.paper',
                        transition: 'box-shadow 0.2s, border-color 0.2s',
                        '&:hover': {
                          boxShadow: 2,
                          borderColor: 'divider',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 2,
                          minWidth: 0,
                          flex: 1,
                          alignItems: 'center',
                        }}
                      >
                        <Box
                          component="img"
                          src={event.coverImage || EVENT_THUMB_PLACEHOLDER}
                          alt=""
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = EVENT_THUMB_PLACEHOLDER;
                          }}
                          sx={{
                            width: 72,
                            height: 72,
                            borderRadius: 2,
                            objectFit: 'cover',
                            flexShrink: 0,
                            bgcolor: 'action.hover',
                          }}
                        />
                        <Box sx={{ minWidth: 0 }}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
                            <Chip
                              label={(event.category || '—').toUpperCase()}
                              size="small"
                              sx={{
                                height: 22,
                                fontWeight: 700,
                                fontSize: 10,
                                letterSpacing: 0.4,
                                bgcolor: 'grey.200',
                                color: 'text.primary',
                              }}
                            />
                            <EventPagarmeStatusChip event={event} />
                            <Typography
                              variant="caption"
                              fontWeight={800}
                              color="error.main"
                              sx={{ letterSpacing: 1 }}
                            >
                              AGUARDANDO
                            </Typography>
                          </Stack>
                          <Typography
                            variant="subtitle1"
                            fontWeight={700}
                            sx={{ lineHeight: 1.3, wordBreak: 'break-word' }}
                          >
                            {event.title}
                          </Typography>
                          <Stack direction="row" spacing={2.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <Place sx={{ fontSize: 18, color: 'text.secondary' }} />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ wordBreak: 'break-word' }}
                              >
                                {parishLine}
                              </Typography>
                            </Stack>
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <CalendarMonth sx={{ fontSize: 18, color: 'text.secondary' }} />
                              <Typography variant="body2" color="text.secondary">
                                {formatDateShortPt(event.startDate)}
                              </Typography>
                            </Stack>
                          </Stack>
                        </Box>
                      </Box>
                      <Box
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        sx={{
                          display: 'flex',
                          flexShrink: 0,
                          justifyContent: 'flex-end',
                        }}
                      >
                        <Stack spacing={0.75} alignItems="flex-end" sx={{ maxWidth: 280 }}>
                          <Stack
                            direction="row"
                            spacing={1.25}
                            alignItems="center"
                            useFlexGap
                            sx={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}
                          >
                            <Tooltip
                              title={PAGARME_APPROVE_BLOCKED_HINT}
                              arrow
                              disableHoverListener={!pagarmeBlocksApprove}
                            >
                              <span>
                                <Button
                                  variant="text"
                                  sx={{
                                    ...sxSoftPillPrimary,
                                    color: 'success.dark',
                                    bgcolor: 'rgba(74, 222, 128, 0.2)',
                                    '&:hover': { bgcolor: 'rgba(74, 222, 128, 0.32)' },
                                  }}
                                  startIcon={
                                    approveEventMutation.isPending && approveEventMutation.variables === event._id ? (
                                      <CircularProgress size={18} color="inherit" />
                                    ) : (
                                      <CheckCircle sx={{ fontSize: 22, color: 'success.dark' }} />
                                    )
                                  }
                                  onClick={() => handleApproveEvent(event)}
                                  disabled={approveDisabled}
                                >
                                  Aprovar
                                </Button>
                              </span>
                            </Tooltip>
                            <IconButton
                              aria-label="Colocar em pausa"
                              sx={sxSoftSquircleWarning}
                              onClick={() => handleDeferPending(event)}
                              disabled={mutationLocked}
                            >
                              {updateStatusMutation.isPending && updateStatusMutation.variables?.id === event._id ? (
                                <CircularProgress size={22} sx={{ color: 'warning.dark' }} />
                              ) : (
                                <Pause sx={{ color: '#b28704' }} />
                              )}
                            </IconButton>
                            <IconButton
                              aria-label="Rejeitar solicitação"
                              sx={sxSoftSquircleDanger}
                              onClick={() => handleRejectEvent(event)}
                              disabled={mutationLocked}
                            >
                              {rejectEventMutation.isPending && rejectEventMutation.variables?.id === event._id ? (
                                <CircularProgress size={22} color="error" />
                              ) : (
                                <CloseIcon sx={{ color: 'error.dark' }} />
                              )}
                            </IconButton>
                          </Stack>
                          {pagarmeBlocksApprove ? (
                            <Typography
                              variant="caption"
                              color="warning.dark"
                              sx={{ textAlign: 'right', lineHeight: 1.35, display: 'block' }}
                            >
                              Recebedor Pagar.me da paróquia ainda não está pronto.
                            </Typography>
                          ) : null}
                        </Stack>
                      </Box>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </CardContent>
        </Card>
      )}

      {/* Lista geral (tabela) — sem pré-publicados quando o status é “Todos” */}
      <Card elevation={0} variant="outlined" sx={{ maxWidth: '100%', overflow: 'hidden' }}>
        <CardHeader
          title={
            <Typography variant="h6" fontWeight={700}>
              Lista geral de eventos
            </Typography>
          }
          subheader="Clique em uma linha para abrir o painel com detalhes, edição e demais ações."
        />
        <CardContent sx={{ px: { xs: 1, sm: 2 }, maxWidth: '100%', boxSizing: 'border-box' }}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : error ? (
            <Alert severity="error">Erro ao carregar eventos</Alert>
          ) : (
            <Box sx={{ width: 1, maxWidth: 1, minWidth: 0, boxSizing: 'border-box' }}>
              {isCompactLayout ? (
                <Stack spacing={1.5} sx={{ width: 1, minWidth: 0 }}>
                  {events.map((event) => (
                    <Paper
                      key={event._id}
                      variant="outlined"
                      elevation={0}
                      role="button"
                      tabIndex={0}
                      aria-label={`Abrir painel do evento: ${event.title}`}
                      onClick={() => openEventQuickPanel(event)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openEventQuickPanel(event);
                        }
                      }}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        cursor: 'pointer',
                        width: 1,
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        transition: 'background-color 0.15s',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ minWidth: 0 }}>
                        <Box
                          component="img"
                          src={event.coverImage || EVENT_THUMB_PLACEHOLDER}
                          alt=""
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = EVENT_THUMB_PLACEHOLDER;
                          }}
                          sx={{
                            width: 52,
                            height: 52,
                            borderRadius: 1,
                            objectFit: 'cover',
                            flexShrink: 0,
                            bgcolor: 'action.hover',
                          }}
                        />
                        <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                          <Typography variant="subtitle2" fontWeight={700} sx={{ wordBreak: 'break-word' }}>
                            {event.title}
                          </Typography>
                          {event.category ? (
                            <Chip
                              label={event.category.toUpperCase()}
                              size="small"
                              sx={{
                                mt: 0.5,
                                height: 20,
                                fontSize: 9,
                                fontWeight: 800,
                                bgcolor: 'grey.700',
                                color: 'common.white',
                              }}
                            />
                          ) : null}
                          <Box sx={{ mt: 0.75 }}>
                            <EventPagarmeStatusChip event={event} />
                          </Box>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, wordBreak: 'break-word' }}>
                            {event.parishName || 'N/A'} · {event.city}, {event.state}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {formatDate(event.startDate)}
                            {event.location ? ` · ${event.location}` : ''}
                          </Typography>
                          <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                            <Alert
                              severity={getStatusMuiSeverity(event.status)}
                              sx={{
                                py: 0,
                                px: 0.75,
                                m: 0,
                                fontSize: 11,
                                '& .MuiAlert-message': { fontWeight: 600, p: 0 },
                              }}
                            >
                              {statusLabels[event.status] || event.status}
                            </Alert>
                            <Typography variant="caption" color="text.secondary">
                              Insc.: {event.totalRegistrations || 0}
                              {event.maxAttendees ? ` / ${event.maxAttendees}` : ''}
                            </Typography>
                            <Typography variant="caption" fontWeight={700} color="primary">
                              {event.totalRevenue?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) ||
                                'R$ 0,00'}
                            </Typography>
                          </Stack>
                        </Box>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <Box
                  sx={{
                    overflowX: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    maxWidth: '100%',
                    '&::-webkit-scrollbar': { height: 8 },
                    '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1', borderRadius: 4 },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#c1c1c1',
                      borderRadius: 4,
                      '&:hover': { backgroundColor: '#a8a8a8' },
                    },
                  }}
                >
                  <Table sx={{ minWidth: 800, '& .MuiTableCell-root': { py: 2, px: 2 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 200, textTransform: 'uppercase', fontSize: 11, fontWeight: 700, color: 'text.secondary' }}>
                      Evento
                    </TableCell>
                    <TableCell sx={{ minWidth: 150, textTransform: 'uppercase', fontSize: 11, fontWeight: 700, color: 'text.secondary' }}>
                      Paróquia
                    </TableCell>
                    <TableCell sx={{ minWidth: 140, textTransform: 'uppercase', fontSize: 11, fontWeight: 700, color: 'text.secondary' }}>
                      Data e local
                    </TableCell>
                    <TableCell sx={{ minWidth: 100, textTransform: 'uppercase', fontSize: 11, fontWeight: 700, color: 'text.secondary' }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ minWidth: 80, textTransform: 'uppercase', fontSize: 11, fontWeight: 700, color: 'text.secondary' }}>
                      Inscrições
                    </TableCell>
                    <TableCell sx={{ minWidth: 100, textTransform: 'uppercase', fontSize: 11, fontWeight: 700, color: 'text.secondary' }}>
                      Receita
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {events.map((event) => (
                    <TableRow
                      key={event._id}
                      hover
                      onClick={() => openEventQuickPanel(event)}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openEventQuickPanel(event);
                        }
                      }}
                      sx={{ cursor: 'pointer' }}
                      aria-label={`Abrir painel do evento: ${event.title}`}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box
                            component="img"
                            src={event.coverImage || EVENT_THUMB_PLACEHOLDER}
                            alt=""
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = EVENT_THUMB_PLACEHOLDER;
                            }}
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 1,
                              objectFit: 'cover',
                              flexShrink: 0,
                              bgcolor: 'action.hover',
                            }}
                          />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography fontWeight={700}>{event.title}</Typography>
                            {event.category ? (
                              <Chip
                                label={event.category.toUpperCase()}
                                size="small"
                                sx={{
                                  mt: 0.75,
                                  height: 22,
                                  fontSize: 10,
                                  fontWeight: 800,
                                  letterSpacing: 0.3,
                                  bgcolor: 'grey.700',
                                  color: 'common.white',
                                }}
                              />
                            ) : null}
                            <Box sx={{ mt: 0.75 }}>
                              <EventPagarmeStatusChip event={event} />
                            </Box>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography>{event.parishName || 'N/A'}</Typography>
                        <Typography variant="caption" color="text.secondary">{event.city}, {event.state}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {formatDate(event.startDate)}
                        </Typography>
                        {event.location ? (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                            {event.location}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Alert 
                          severity={getStatusMuiSeverity(event.status)} 
                          sx={{ 
                            p: 0.5, 
                            m: 0, 
                            fontSize: 12,
                            minWidth: 'fit-content',
                            '& .MuiAlert-message': {
                              fontWeight: 500
                            }
                          }}
                        >
                          {statusLabels[event.status] || event.status}
                        </Alert>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {event.totalRegistrations || 0}
                        </Typography>
                        {event.maxAttendees && (
                          <Typography variant="caption" color="text.secondary">
                            / {event.maxAttendees}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={700} color="primary">
                          {event.totalRevenue?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) || 'R$ 0,00'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
                </Box>
              )}
              {pagination && (
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mt={2}
                  flexWrap="wrap"
                  gap={2}
                  sx={{ width: 1, minWidth: 0 }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                    Exibindo <strong>{events.length}</strong> de <strong>{pagination.total}</strong> eventos
                  </Typography>
                  {pagination.pages > 1 ? (
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                        Página {pagination.current} / {pagination.pages}
                      </Typography>
                      <Button
                        size="small"
                        disabled={pagination.current === 1}
                        onClick={() => setFilters((prev) => ({ ...prev, page: (prev.page || 1) - 1 }))}
                      >
                        Anterior
                      </Button>
                      <Button
                        size="small"
                        disabled={pagination.current === pagination.pages}
                        onClick={() => setFilters((prev) => ({ ...prev, page: (prev.page || 1) + 1 }))}
                      >
                        Próxima
                      </Button>
                    </Box>
                  ) : null}
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Painel rápido ao clicar na linha: resumo + ações (soft UI) */}
      <Dialog
        open={Boolean(quickActionsEvent)}
        onClose={closeEventQuickPanel}
        maxWidth="sm"
        fullWidth
        fullScreen={isCompactLayout}
        scroll="body"
        sx={{ '& .MuiDialog-paper': { m: { xs: 0, sm: 2 }, maxWidth: { xs: '100%', sm: '600px' } } }}
      >
        {quickActionsEvent && (
          <>
            <DialogTitle
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'flex-start' },
                justifyContent: 'space-between',
                gap: 2,
                pr: { xs: 2, sm: 1 },
                pt: { xs: 2, sm: 2 },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  minWidth: 0,
                  flex: 1,
                  alignItems: 'flex-start',
                  flexDirection: { xs: 'column', sm: 'row' },
                }}
              >
                <Box
                  component="img"
                  src={quickActionsEvent.coverImage || EVENT_THUMB_PLACEHOLDER}
                  alt=""
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = EVENT_THUMB_PLACEHOLDER;
                  }}
                  sx={{
                    width: { xs: 80, sm: 72 },
                    height: { xs: 80, sm: 72 },
                    borderRadius: 2,
                    objectFit: 'cover',
                    flexShrink: 0,
                    bgcolor: 'action.hover',
                    alignSelf: { xs: 'center', sm: 'flex-start' },
                  }}
                />
                <Box sx={{ minWidth: 0, width: { xs: 1, sm: 'auto' } }}>
                  <Typography
                    variant="h6"
                    component="div"
                    sx={{ lineHeight: 1.3, wordBreak: 'break-word' }}
                  >
                    {quickActionsEvent.title}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                    <Alert
                      severity={getStatusMuiSeverity(quickActionsEvent.status)}
                      icon={false}
                      sx={{
                        py: 0,
                        px: 1,
                        fontSize: 12,
                        fontWeight: 600,
                        display: 'inline-flex',
                        alignItems: 'center',
                      }}
                    >
                      {statusLabels[quickActionsEvent.status] || quickActionsEvent.status}
                    </Alert>
                    {quickActionsEvent.category && (
                      <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                        {quickActionsEvent.category}
                      </Typography>
                    )}
                  </Stack>
                </Box>
              </Box>
              <IconButton
                aria-label="Fechar painel"
                onClick={closeEventQuickPanel}
                size="small"
                sx={{ alignSelf: { xs: 'flex-end', sm: 'flex-start' } }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={1.25}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Paróquia:</strong> {quickActionsEvent.parishName || 'N/A'} · {quickActionsEvent.city},{' '}
                  {quickActionsEvent.state}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Data:</strong> {formatDateTime(quickActionsEvent.startDate)}
                </Typography>
                {quickActionsEvent.location && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Local:</strong> {quickActionsEvent.location}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  <strong>Inscrições:</strong> {quickActionsEvent.totalRegistrations || 0}
                  {quickActionsEvent.maxAttendees != null && ` / ${quickActionsEvent.maxAttendees}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Receita:</strong>{' '}
                  {quickActionsEvent.totalRevenue?.toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  }) || 'R$ 0,00'}
                </Typography>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Pagamentos
                  </Typography>
                  <EventPagarmeStatusChip event={quickActionsEvent} />
                </Box>
              </Stack>

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 1.5 }}>
                Ações rápidas
              </Typography>

              {quickActionsEvent.status === 'pre_published' && (
                <Box sx={{ mb: 2 }}>
                  <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Tooltip
                      title={PAGARME_APPROVE_BLOCKED_HINT}
                      arrow
                      disableHoverListener={!isApproveBlockedByParishPagarme(quickActionsEvent)}
                    >
                      <span>
                        <Button
                          variant="text"
                          sx={sxSoftPillPrimary}
                          startIcon={
                            approveEventMutation.isPending && approveEventMutation.variables === quickActionsEvent._id ? (
                              <CircularProgress size={18} color="inherit" />
                            ) : (
                              <CheckCircle />
                            )
                          }
                          onClick={() => {
                            const ev = quickActionsEvent;
                            closeEventQuickPanel();
                            handleApproveEvent(ev);
                          }}
                          disabled={
                            approveEventMutation.isPending ||
                            rejectEventMutation.isPending ||
                            updateStatusMutation.isPending ||
                            isApproveBlockedByParishPagarme(quickActionsEvent)
                          }
                        >
                          Aprovar
                        </Button>
                      </span>
                    </Tooltip>
                    <IconButton
                      aria-label="Colocar em pausa"
                      sx={sxSoftSquircleWarning}
                      onClick={() => {
                        const ev = quickActionsEvent;
                        closeEventQuickPanel();
                        handleDeferPending(ev);
                      }}
                      disabled={
                        approveEventMutation.isPending ||
                        rejectEventMutation.isPending ||
                        updateStatusMutation.isPending
                      }
                    >
                      {updateStatusMutation.isPending && updateStatusMutation.variables?.id === quickActionsEvent._id ? (
                        <CircularProgress size={22} sx={{ color: 'warning.dark' }} />
                      ) : (
                        <Pause />
                      )}
                    </IconButton>
                    <IconButton
                      aria-label="Rejeitar solicitação"
                      sx={sxSoftSquircleDanger}
                      onClick={() => {
                        const ev = quickActionsEvent;
                        closeEventQuickPanel();
                        handleRejectEvent(ev);
                      }}
                      disabled={
                        approveEventMutation.isPending ||
                        rejectEventMutation.isPending ||
                        updateStatusMutation.isPending
                      }
                    >
                      {rejectEventMutation.isPending && rejectEventMutation.variables?.id === quickActionsEvent._id ? (
                        <CircularProgress size={22} color="error" />
                      ) : (
                        <CloseIcon />
                      )}
                    </IconButton>
                  </Stack>
                  {isApproveBlockedByParishPagarme(quickActionsEvent) ? (
                    <Alert severity="warning" sx={{ mt: 1.5, py: 0.5 }}>
                      Aprovação indisponível até o recebedor Pagar.me da paróquia estar sincronizado e pronto.
                    </Alert>
                  ) : null}
                </Box>
              )}

              {quickActionsEvent.status === 'published' && (
                <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                  <IconButton
                    aria-label="Pausar evento"
                    sx={sxSoftSquircleWarning}
                    onClick={() => {
                      const ev = quickActionsEvent;
                      closeEventQuickPanel();
                      handleStatusChange(ev, 'draft');
                    }}
                  >
                    <Pause />
                  </IconButton>
                  <IconButton
                    aria-label="Cancelar evento"
                    sx={sxSoftSquircleDanger}
                    onClick={() => {
                      const ev = quickActionsEvent;
                      closeEventQuickPanel();
                      handleStatusChange(ev, 'cancelled');
                    }}
                  >
                    <Cancel />
                  </IconButton>
                </Stack>
              )}

              {quickActionsEvent.status === 'draft' && (
                <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                  <Button
                    variant="text"
                    sx={sxSoftPillPrimary}
                    startIcon={<PlayArrow />}
                    onClick={() => {
                      const ev = quickActionsEvent;
                      closeEventQuickPanel();
                      handleStatusChange(ev, 'published');
                    }}
                  >
                    Publicar
                  </Button>
                  <IconButton
                    aria-label="Cancelar evento"
                    sx={sxSoftSquircleDanger}
                    onClick={() => {
                      const ev = quickActionsEvent;
                      closeEventQuickPanel();
                      handleStatusChange(ev, 'cancelled');
                    }}
                  >
                    <Cancel />
                  </IconButton>
                </Stack>
              )}

              {quickActionsEvent.status === 'paused' && (
                <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                  <Button
                    variant="text"
                    sx={sxSoftPillPrimary}
                    startIcon={<PlayArrow />}
                    onClick={() => {
                      const ev = quickActionsEvent;
                      closeEventQuickPanel();
                      handleStatusChange(ev, 'published');
                    }}
                  >
                    Publicar
                  </Button>
                  <IconButton
                    aria-label="Cancelar evento"
                    sx={sxSoftSquircleDanger}
                    onClick={() => {
                      const ev = quickActionsEvent;
                      closeEventQuickPanel();
                      handleStatusChange(ev, 'cancelled');
                    }}
                  >
                    <Cancel />
                  </IconButton>
                </Stack>
              )}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<Visibility />}
                  fullWidth
                  onClick={() => goToFullDetailsFromQuickPanel(quickActionsEvent)}
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  Ver detalhes completos
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<Edit />}
                  fullWidth
                  onClick={() => goToEditFromQuickPanel(quickActionsEvent)}
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  Editar evento
                </Button>
              </Stack>
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* Dialog de detalhes */}
      <Dialog open={showDetailsDialog} onClose={() => setShowDetailsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Detalhes do Evento</DialogTitle>
        <DialogContent dividers>
          {selectedEvent && (
            <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2}>
              <Box>
                <Typography variant="subtitle2">Título</Typography>
                <Typography>{selectedEvent.title}</Typography>
                <Typography variant="subtitle2" mt={2}>Status</Typography>
                <Alert severity={getStatusMuiSeverity(selectedEvent.status)} sx={{ p: 0.5, m: 0, fontSize: 12 }}>{statusLabels[selectedEvent.status] || selectedEvent.status}</Alert>
                <Typography variant="subtitle2" mt={2}>Categoria</Typography>
                <Typography>{selectedEvent.category || 'Não informado'}</Typography>
                <Typography variant="subtitle2" mt={2}>Paróquia</Typography>
                <Typography>{selectedEvent.parishName || 'N/A'}</Typography>
                <Typography variant="subtitle2" mt={2}>Data de Início</Typography>
                <Typography>{formatDateTime(selectedEvent.startDate)}</Typography>
                {selectedEvent.endDate && <><Typography variant="subtitle2" mt={2}>Data de Término</Typography><Typography>{formatDateTime(selectedEvent.endDate)}</Typography></>}
                <Typography variant="subtitle2" mt={2}>Local</Typography>
                <Typography>{selectedEvent.location || 'Não informado'}</Typography>
                <Typography variant="subtitle2" mt={2}>Criado em</Typography>
                <Typography>{formatDateTime(selectedEvent.createdAt)}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2">Descrição</Typography>
                <Typography>{selectedEvent.description || 'Sem descrição'}</Typography>
                <Typography variant="subtitle2" mt={2}>Inscrições</Typography>
                <Typography>{selectedEvent.totalRegistrations || 0}{selectedEvent.maxAttendees && ` / ${selectedEvent.maxAttendees}`}</Typography>
                <Typography variant="subtitle2" mt={2}>Receita Total</Typography>
                <Typography>{selectedEvent.totalRevenue?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) || 'R$ 0,00'}</Typography>
                <Typography variant="subtitle2" mt={2}>Participantes Atuais</Typography>
                <Typography>{selectedEvent.totalRegistrations || 0}</Typography>
                {selectedEvent.tags && selectedEvent.tags.length > 0 && (
                  <Box mt={2}>
                    <Typography variant="subtitle2">Tags</Typography>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {selectedEvent.tags.map((tag, idx) => (
                        <Alert key={idx} severity="info" sx={{ p: 0.5, m: 0, fontSize: 12 }}>{tag}</Alert>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDetailsDialog(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de alteração de status */}
      <Dialog open={showStatusDialog} onClose={() => setShowStatusDialog(false)}>
        <DialogTitle>Alterar Status do Evento</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Confirme a alteração do status do evento "{selectedEvent?.title}".
          </DialogContentText>
          <Box mt={2}>
            <Typography variant="body2">
              Status atual: <Alert severity={getStatusMuiSeverity(selectedEvent?.status || '')} sx={{ p: 0.5, m: 0, fontSize: 12, display: 'inline-block' }}>{statusLabels[selectedEvent?.status || '']}</Alert>
            </Typography>
            <Typography variant="body2">
              Novo status: <Alert severity={getStatusMuiSeverity(newStatus)} sx={{ p: 0.5, m: 0, fontSize: 12, display: 'inline-block' }}>{statusLabels[newStatus]}</Alert>
            </Typography>
            {newStatus === 'cancelled' && (
              <TextField
                label="Motivo do cancelamento"
                fullWidth
                multiline
                minRows={2}
                value={statusReason}
                onChange={(e) => setStatusReason(e.target.value)}
                sx={{ mt: 2 }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowStatusDialog(false)}>Cancelar</Button>
          <Button 
            onClick={handleConfirmStatusChange}
            disabled={
              updateStatusMutation.isPending || 
              rejectEventMutation.isPending || 
              (newStatus === 'cancelled' && !statusReason.trim())
            }
            color="primary"
            variant="contained"
          >
            {(updateStatusMutation.isPending || rejectEventMutation.isPending) ? 'Processando...' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Programação */}
      <Dialog open={showScheduleDialog} onClose={() => setShowScheduleDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Programação do Evento</DialogTitle>
        <DialogContent dividers>
          {scheduleItems.map((item, idx) => (
            <Box key={item.id} display="flex" gap={2} alignItems="center" mb={2}>
              <TextField
                label="Título"
                value={item.title}
                onChange={e => handleChangeScheduleItem(item.id, 'title', e.target.value)}
                size="small"
                fullWidth
              />
              <TextField
                label="Data/Hora"
                type="datetime-local"
                value={item.dateTime}
                onChange={e => handleChangeScheduleItem(item.id, 'dateTime', e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Local"
                value={item.location}
                onChange={e => handleChangeScheduleItem(item.id, 'location', e.target.value)}
                size="small"
              />
              <IconButton color="error" onClick={() => handleRemoveScheduleItem(item.id)}>
                <Cancel />
              </IconButton>
            </Box>
          ))}
          <Button onClick={handleAddScheduleItem} variant="outlined" color="primary" sx={{ mt: 2 }}>
            Adicionar Item
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowScheduleDialog(false)}>Fechar</Button>
          {/* Aqui futuramente: botão para salvar programação no backend */}
        </DialogActions>
      </Dialog>

      {/* Diálogo de Confirmação de Aprovação/Rejeição */}
      <Dialog
        open={approvalDialog.open}
        onClose={() => setApprovalDialog({ open: false, event: null, action: null })}
        maxWidth="sm"
        fullWidth
        fullScreen={isCompactLayout}
        scroll="paper"
        sx={{
          '& .MuiDialog-paper': {
            m: { xs: 0, sm: 2 },
            maxHeight: { xs: '100%', sm: 'calc(100% - 64px)' },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, sm: 2 },
            flexWrap: 'wrap',
            pr: { xs: 2, sm: 3 },
            fontSize: { xs: '1rem', sm: '1.25rem' },
            color: approvalDialog.action === 'approve' ? 'success.main' : 'error.main',
          }}
        >
          {approvalDialog.action === 'approve' ? (
            <>
              <PlayArrow color="success" sx={{ flexShrink: 0 }} />
              <span>Confirmar Aprovação</span>
            </>
          ) : (
            <>
              <Cancel color="error" sx={{ flexShrink: 0 }} />
              <span>Confirmar Rejeição</span>
            </>
          )}
        </DialogTitle>

        <DialogContent
          dividers={isCompactLayout}
          sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2 } }}
        >
          <DialogContentText sx={{ mb: 2, wordBreak: 'break-word' }} component="div">
            {approvalDialog.action === 'approve' ? (
              <>
                Você está prestes a <strong>aprovar e publicar</strong> o evento:
                <Typography
                  variant="h6"
                  component="span"
                  color="primary"
                  sx={{ mt: 1, display: 'block', fontSize: { xs: '1rem', sm: '1.25rem' }, wordBreak: 'break-word' }}
                >
                  &ldquo;{approvalDialog.event?.title}&rdquo;
                </Typography>
                Após a aprovação, o evento ficará <strong>disponível para o público</strong> e poderá receber
                inscrições.
              </>
            ) : (
              <>
                Você está prestes a <strong>rejeitar</strong> o evento:
                <Typography
                  variant="h6"
                  component="span"
                  color="error"
                  sx={{ mt: 1, display: 'block', fontSize: { xs: '1rem', sm: '1.25rem' }, wordBreak: 'break-word' }}
                >
                  &ldquo;{approvalDialog.event?.title}&rdquo;
                </Typography>
                O evento será <strong>removido da lista de aprovação</strong> e não ficará disponível para o
                público.
              </>
            )}
          </DialogContentText>

          {approvalDialog.event && (
            <Box
              sx={{
                p: { xs: 1.5, sm: 2 },
                backgroundColor: 'grey.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.200',
                overflow: 'hidden',
                maxWidth: '100%',
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Detalhes do Evento:
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                <strong>Paróquia:</strong> {approvalDialog.event.parishName || 'N/A'}
                <br />
                <strong>Data:</strong> {formatDate(approvalDialog.event.startDate)}
                <br />
                <strong>Local:</strong> {approvalDialog.event.location || 'Não informado'}
                <br />
                <strong>Categoria:</strong> {approvalDialog.event.category || 'Não informado'}
              </Typography>
            </Box>
          )}

          {approvalDialog.action === 'approve' &&
          approvalDialog.event &&
          isApproveBlockedByParishPagarme(approvalDialog.event) ? (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {PAGARME_APPROVE_BLOCKED_HINT}
            </Alert>
          ) : null}
        </DialogContent>

        <DialogActions
          sx={{
            p: { xs: 2, sm: 3 },
            gap: 1,
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: { xs: 'stretch', sm: 'flex-end' },
            '& .MuiButton-root': { width: { xs: '100%', sm: 'auto' } },
          }}
        >
          <Button
            onClick={() => setApprovalDialog({ open: false, event: null, action: null })}
            disabled={approveEventMutation.isPending || rejectEventMutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            onClick={confirmApproval}
            variant="contained"
            disabled={
              approveEventMutation.isPending ||
              rejectEventMutation.isPending ||
              (approvalDialog.action === 'approve' &&
                !!approvalDialog.event &&
                isApproveBlockedByParishPagarme(approvalDialog.event))
            }
            color={approvalDialog.action === 'approve' ? 'success' : 'error'}
            startIcon={
              approveEventMutation.isPending || rejectEventMutation.isPending ? (
                <CircularProgress size={16} color="inherit" />
              ) : approvalDialog.action === 'approve' ? (
                <PlayArrow />
              ) : (
                <Cancel />
              )
            }
          >
            {approveEventMutation.isPending || rejectEventMutation.isPending
              ? 'Processando...'
              : approvalDialog.action === 'approve'
                ? 'Aprovar Evento'
                : 'Rejeitar Evento'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de edição */}
      <Dialog 
        open={showEditDialog} 
        onClose={() => {
          setShowEditDialog(false);
          setEditingEvent(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Editar Evento</DialogTitle>
        <DialogContent dividers>
          {editingEvent && (
            <Box>
              <DialogContentText gutterBottom>
                Você está prestes a editar o evento: <strong>{editingEvent.title}</strong>
              </DialogContentText>
              <Alert severity="info" sx={{ mt: 2 }}>
                A edição será feita através de uma página dedicada. 
                Clique em "Abrir Editor" para continuar.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowEditDialog(false);
            setEditingEvent(null);
          }}>
            Cancelar
          </Button>
          <Button 
            variant="contained"
            color="primary"
            onClick={() => {
              if (editingEvent) {
                // Redirecionar para página de edição (rota relativa dentro do layout)
                navigate(`/events/edit/${editingEvent._id}`);
                setShowEditDialog(false);
                setEditingEvent(null);
              }
            }}
          >
            Abrir Editor
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar de feedback */}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Events; 